/**
 * ConversationAttachmentsList — what is attached to a conversation (artifacts, memories, tasks, other conversations),
 * grouped by purpose and by type, with attach and detach. Demo page: /chats (context column).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Paperclip, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import {
  attachToConversationMutation,
  detachFromConversationMutation,
  listConversationAttachmentsOptions,
  listConversationAttachmentsQueryKey,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { ConversationAttachment } from '../../generated/api/types.gen.js';
import { Can } from '../auth/Can.js';
import { Button } from '../ui/Button.js';
import { FieldLabel } from '../ui/FieldLabel.js';
import { Input, Select } from '../ui/Input.js';
import { Modal } from '../ui/Modal.js';
import { Skeleton } from '../ui/Skeleton.js';
import {
  attachmentPurpose,
  attachmentReason,
  type AttachedType,
  type AttachmentRole,
  type AttachmentPurpose,
} from '../../lib/conversation-operating-layer.js';

// ─── Types ─────────────────────────────────────────────────────────────────────

/** A stored attachment of a kind this list knows (the API also holds project and workflow attachments). */
type AttachmentRow = ConversationAttachment & { attachedType: AttachedType };

function isKnownAttachment(attachment: ConversationAttachment): attachment is AttachmentRow {
  return ATTACHMENT_TYPES.some((type) => type === attachment.attachedType);
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<AttachedType, string> = {
  artifact: 'Artifacts',
  memory: 'Memories',
  task: 'Tasks',
  conversation: 'Conversations',
};

const PURPOSE_LABELS: Record<AttachmentPurpose, string> = {
  generated: 'Generated artifacts',
  attached: 'Attached context',
  referenced: 'References and dependencies',
  handoff: 'Handoffs',
};

const ATTACHMENT_TYPES: AttachedType[] = ['artifact', 'memory', 'task', 'conversation'];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function groupBy<T>(arr: T[], key: (item: T) => string): Record<string, T[]> {
  return arr.reduce(
    (acc, item) => {
      const k = key(item);
      if (!acc[k]) acc[k] = [];
      acc[k].push(item);
      return acc;
    },
    {} as Record<string, T[]>,
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ─── Attach picker ────────────────────────────────────────────────────────────

interface AttachPickerProps {
  conversationId: string;
  onClose: () => void;
}

const ROLES: AttachmentRole[] = ['reference', 'context', 'dependency', 'handoff-payload'];

function AttachPicker({ conversationId, onClose }: AttachPickerProps) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<AttachedType>('artifact');
  const [refId, setRefId] = useState('');
  const [role, setRole] = useState<AttachmentRole>('reference');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    ...attachToConversationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConversationAttachmentsQueryKey({ path: { id: conversationId } }),
      });
      onClose();
    },
    onError: (err) => setError(err.message || 'Could not attach the item.'),
  });

  const handleSubmit = () => {
    setError(null);
    const numericId = Number(refId.trim());
    if (!refId.trim() || Number.isNaN(numericId)) {
      setError('The ID must be a number.');
      return;
    }
    mutation.mutate({ path: { id: conversationId }, body: { attachedType: type, attachedId: numericId, role } });
  };

  return (
    <Modal open onClose={onClose} title="Attach item" maxWidth="sm" dismissable={!mutation.isPending}>
      <div className="space-y-3">
        <div>
          <FieldLabel label="Type" htmlFor="attach-type" />
          <Select id="attach-type" value={type} onChange={(e) => setType(e.target.value as AttachedType)}>
            {ATTACHMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <FieldLabel label="ID" htmlFor="attach-id" required />
          <Input
            id="attach-id"
            placeholder="Numeric ID of the item"
            value={refId}
            onChange={(e) => setRefId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && refId.trim()) handleSubmit();
            }}
            required
          />
        </div>
        <div>
          <FieldLabel label="Role" htmlFor="attach-role" />
          <Select id="attach-role" value={role} onChange={(e) => setRole(e.target.value as AttachmentRole)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </div>
        {error && <p className="text-xs text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={mutation.isPending} disabled={!refId.trim()} onClick={handleSubmit}>
            Attach
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Attachment bucket ────────────────────────────────────────────────────────

interface BucketProps {
  label: string;
  items: AttachmentRow[];
  conversationId: string;
}

function AttachmentBucket({ label, items, conversationId }: BucketProps) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const detachMutation = useMutation({
    ...detachFromConversationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConversationAttachmentsQueryKey({ path: { id: conversationId } }),
      });
    },
  });

  return (
    <div className="overflow-hidden rounded-lg border border-card-border">
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between rounded-none bg-background-gray-secondary_alt"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          {label} ({items.length})
        </span>
        {open ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
      </Button>
      {open && (
        <ul className="divide-y divide-card-border">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate text-xs text-text-secondary">ID: {a.attachedId}</span>
                <span className="block truncate text-[10px] text-text-tertiary">{attachmentReason(a)}</span>
              </span>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-[10px] text-text-tertiary">
                  {a.role} · {formatDate(a.attachedAt)}
                </span>
                <Can policy="CanManageAgents">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    className="hover:text-danger"
                    onClick={() => detachMutation.mutate({ path: { id: conversationId, attachmentId: a.id } })}
                    disabled={detachMutation.isPending}
                    aria-label={`Detach ${a.attachedType} ${a.attachedId}`}
                  >
                    <Trash2 size={12} aria-hidden />
                  </Button>
                </Can>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  conversationId: string;
}

export function ConversationAttachmentsList({ conversationId }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data, isLoading } = useQuery(listConversationAttachmentsOptions({ path: { id: conversationId } }));

  const attachments = (data ?? []).filter(isKnownAttachment);
  const buckets = groupBy(attachments, (a) => a.attachedType);
  const purposeBuckets = groupBy(attachments, (a) => attachmentPurpose(a));

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold tracking-wider text-text-tertiary uppercase">
          <Paperclip size={12} aria-hidden />
          Attachments
        </h3>
        <Can policy="CanManageAgents">
          <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)} aria-label="Attach item">
            <Plus size={12} aria-hidden />
            Attach
          </Button>
        </Can>
      </div>

      {isLoading && <Skeleton height={32} rounded="rounded-lg" />}

      {!isLoading && attachments.length === 0 && (
        <p className="text-xs text-text-tertiary">No attachments. Use the Attach button to add context.</p>
      )}

      <div className="space-y-2">
        {(Object.keys(PURPOSE_LABELS) as AttachmentPurpose[])
          .filter((purpose) => purposeBuckets[purpose]?.length)
          .map((purpose) => (
            <div
              key={purpose}
              className="rounded-lg border border-card-border bg-background-gray-secondary_alt px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-title-50">{PURPOSE_LABELS[purpose]}</span>
                <span className="rounded-sm bg-surface px-1.5 py-0.5 text-[10px] text-text-tertiary">
                  {purposeBuckets[purpose].length}
                </span>
              </div>
              <p className="mt-1 text-[11px] leading-4 text-text-tertiary">
                {purposeBuckets[purpose]
                  .slice(0, 3)
                  .map((attachment) => `${attachment.attachedType} ${attachment.attachedId}`)
                  .join(', ')}
              </p>
            </div>
          ))}
        {(Object.keys(TYPE_LABELS) as AttachedType[])
          .filter((t) => buckets[t]?.length)
          .map((t) => (
            <AttachmentBucket key={t} label={TYPE_LABELS[t]} items={buckets[t]} conversationId={conversationId} />
          ))}
      </div>

      {pickerOpen && <AttachPicker conversationId={conversationId} onClose={() => setPickerOpen(false)} />}
    </section>
  );
}
